import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { ManageBriefTabsService } from '../../service/manage-brief-tabs/manage-brief-tabs.service';
import { StatsOverviewComponent } from './atoms/stats-overview/stats-overview.component';
import { SentimentComponent } from './atoms/sentiment/sentiment.component';
import { BriefAccuracyComponent } from './atoms/brief-accuracy/brief-accuracy.component';
import { ContentPerformanceComponent } from './atoms/content-performance/content-performance.component';
import { TopPerformersComponent } from './atoms/top-performers/top-performers.component';
import { ActivatedRoute } from '@angular/router';
import { BriefResponseDto } from '../../../../common/general-types/briefs.dto';
import { SubmittedPostDto, SubmittedPostVersionEnum } from '../../../../common/general-types/submitted-posts.dto';
import { of, Subscription, take, firstValueFrom } from 'rxjs';
import { SubmittedPostsService } from '../../../../services/submitted-posts/submitted-posts.service';
import { SuperSocialService } from '../../../../services/super-social/super-social.service';
import { LatestPostResultsDto, ResultDto } from '../../../../common/general-types/super-social.dto';
import { LoadingInlineComponent } from '../../../../atoms/loading-inline/loading-inline.component';
import { VideoFeedbackComponent } from '@/app/pages/view-submission/molecules/video-feedback/video-feedback.component';
import { CreatorResponseDto } from '@/app/common/general-types/creators.dto';
import { SocialChannelEnum } from '@/app/common/general-types/briefs.dto';
import { creatorIdFromManageBriefDetail } from '../../manage-brief-tab.routing';
import { UltraSocialService } from '@/app/services/ultra-social/ultra-social';
import { PhylloResultsService } from '@/app/services/phyllo-results/phyllo-results.service';
import { ReportScheduleBanner } from './molecules/report-schedule/report-schedule-banner/report-schedule-banner';
import { ReportScheduleDialog, ReportScheduleDialogData, ReportScheduleDialogResult } from './molecules/report-schedule/report-schedule-dialog/report-schedule-dialog';
import { ReportScheduleService } from './molecules/report-schedule/report-schedule.service';
import { ReportScheduleDto } from './molecules/report-schedule/report-schedule.types';
import { browserTimeZone, schedulePillLabel } from './molecules/report-schedule/report-schedule.helpers';
import { isScheduledReportsEnabled } from '@/app/common/app-config/scheduled-reports';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import { RefreshResultsToastService } from '@/app/atoms/refresh-results-toast/refresh-results-toast.service';
import { PosthogService } from '@/app/services/analytics/posthog.service';
import { FirebaseService } from '@/app/common/firebase/firebase.service';
import { ChangeDetectorRef } from '@angular/core';
import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import {
  applyCreatorNamesToResults,
  BriefResultsDto,
  filterPiecePosts,
  pieceChannels,
  pieceLabels,
  piecePlacements,
  pieceResultsFor,
  applyPhylloMetricsToResults,
  dedupeReportingResultsBySubmittedPost,
  indexSuperSocialResultsBySubmittedPost,
  mergeSubmittedPostsIntoResults,
  platformFilterChannelsFromBrief,
  ReportingResultsMap,
  reportingResultKey,
  resultForSubmittedPost,
  storySubmittedPostIds,
  superSocialLookupIds,
} from './reporting.helpers';

const REPORT_SCHEDULE_MANAGER_ROLES = [4, 6, 77];

@Component({
  selector: 'mbt-reporting',
  standalone: true,
  imports: [StatsOverviewComponent, SentimentComponent, BriefAccuracyComponent,
    ContentPerformanceComponent, TopPerformersComponent, LoadingInlineComponent,
    VideoFeedbackComponent, ReportScheduleBanner, ReportScheduleDialog],
  templateUrl: './reporting.component.html',
  styleUrl: './reporting.component.css'
})
export class ReportingComponent implements OnInit, OnDestroy {

  SocialChannelEnum = SocialChannelEnum;

  resultsViewTab = signal<0 | 1 | 2 | 3>(2); // 0: Sentiment, 1: Brief Accuracy, 2: Content Performance, 3: Top Performers

  focussedBriefId = signal<string | undefined>(undefined);
  focussedBrief = signal<BriefResponseDto | undefined>(undefined);


  isUgcOnly = signal<boolean>(false);
  invalidSubmittedPosts = signal<SubmittedPostDto[] | undefined>(undefined);
  submittedPosts = signal<SubmittedPostDto[] | undefined>(undefined);

  selectedChannelTab = signal<number>(-1);

  /** A brief built from pieces has no shout brief: its posts and results are read by brief id. */
  readonly isPieceBrief = computed(() => !!this.focussedBrief()?.pieces?.length);
  private readonly placements = computed(() => piecePlacements(this.focussedBrief()?.pieces));
  readonly pieceLabels = computed(() => pieceLabels(this.placements()));
  /** null is every piece. */
  readonly selectedPiece = signal<string | null>(null);
  private readonly pieceResults = signal<BriefResultsDto | undefined>(undefined);

  platformFilterChannels = computed<number[]>(() =>
    this.isPieceBrief()
      ? pieceChannels(this.placements())
      : platformFilterChannelsFromBrief(this.focussedBrief()?.shoutBrief),
  );

  readonly showChannelTabs = computed(() =>
    this.isPieceBrief()
      ? this.platformFilterChannels().length > 1
      : !!this.focussedBrief()?.shoutBrief?.crossPlatforms?.length,
  );

  shoutSuperSocialResultsRes = signal<{ results: any, submittedPosts: SubmittedPostDto[] } | undefined>(undefined);
  shoutUltraSocialResultsRes = signal<{ results: any, submittedPosts: SubmittedPostDto[] } | undefined>(undefined);


  isLoadingInitialData = signal<boolean>(true);
  noContentForReporting = signal<boolean>(false);

  isSwitchingChannel = signal<boolean>(false);
  noResultsForChannel = signal<boolean>(false);

  videoId = signal<string | undefined>(undefined);
  creator = signal<CreatorResponseDto | undefined>(undefined);
  briefId = signal<string | undefined>(undefined);

  /** Services */
  private route = inject(ActivatedRoute);
  public manageBriefTabsService = inject(ManageBriefTabsService);
  public submittedPostsService = inject(SubmittedPostsService);
  public superSocialService = inject(SuperSocialService);
  public ultraSocialService = inject(UltraSocialService);
  private phylloResultsService = inject(PhylloResultsService);
  private readonly reportSchedules = inject(ReportScheduleService);
  private readonly toast = inject(ToastNotificationService);
  private readonly refreshResultsToasts = inject(RefreshResultsToastService);
  private readonly posthog = inject(PosthogService);
  private readonly firebase = inject(FirebaseService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly currentUser = inject(CurrentUserService);

  readonly reportsEnabled = isScheduledReportsEnabled();
  readonly reportSchedule = signal<ReportScheduleDto | null>(null);
  readonly scheduleDialog = signal<ReportScheduleDialogData | null>(null);
  readonly schedulePillLabel = computed(() => {
    const schedule = this.reportSchedule();
    return schedule ? schedulePillLabel(schedule.dayOfWeek, schedule.timeOfDay) : 'Schedule report';
  });
  private openedScheduleFromQuery = false;
  /** Services - END */

  subscriptionList = new Subscription();
  private routeCreatorId = signal<string | undefined>(undefined);

  ngOnInit(): void {
    console.log('🚨🚨🚨🚨🚨🚨🚨🚨🚨🚨🚨🚨🚨 Reporting -> ngOnInit');
    this.focussedBriefId.set(this.route.snapshot.params['briefId']);
    this.subscriptionList.add(
      this.route.paramMap.subscribe((params) => {
        const creatorId = creatorIdFromManageBriefDetail(params.get('subTab'));
        this.routeCreatorId.set(creatorId);
        if (!creatorId) {
          this.videoId.set(undefined);
          this.creator.set(undefined);
          this.briefId.set(undefined);
          return;
        }
        this.tryOpenFeedbackForCreator(creatorId);
      }),
    );
    if (this.focussedBriefId()) {
      this.getBrief();
    }
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  getBrief() {
    if (this.focussedBriefId()) {
      console.log('Reporting Brief ----> ', this.manageBriefTabsService.focussedBrief());
      this.focussedBrief.set(this.manageBriefTabsService.focussedBrief());

      if (this.focussedBrief() && this.focussedBrief()!.shoutBrief) {
        const crossPlatforms = this.focussedBrief()!.shoutBrief!.crossPlatforms;
        if (crossPlatforms && crossPlatforms.length > 0) {
          this.selectedChannelTab.set(-1);
        } else {
          this.selectedChannelTab.set(this.focussedBrief()!.shoutBrief!.socialPlatform);
        }
      }

      this.getSubmittedPosts();
      this.loadReportSchedule();
    }
  }

  getSubmittedPosts(isChannelSwitch = false) {
    if (this.isPieceBrief()) {
      this.getPieceResults(isChannelSwitch);
      return;
    }
    if (this.focussedBrief() && this.focussedBrief()!.shoutBrief) {
      const validSubmittedPostsSub = this.submittedPostsService.getValidSubmittedPostsForShoutBriefId(this.focussedBrief()!.shoutBrief!.id!, this.selectedChannelTab()).pipe(take(1)).subscribe((submittedPosts: SubmittedPostDto[]) => {
        console.log('Reporting -> getSubmittedPosts -> submittedPosts: ', submittedPosts);
        this.submittedPosts.set(submittedPosts);

        console.log('🧩🧩🧩 ReportingComponent -> getSubmittedPosts -> SUBMITTED POSTS: ', submittedPosts);

        if (submittedPosts && submittedPosts.length > 0) {

          if (this.focussedBrief()!.shoutBrief!.resultsVersion === SubmittedPostVersionEnum.UltraSocial) {
            this.getUltraSocialResults(isChannelSwitch);
          } else {
            this.getSuperSocialResults(isChannelSwitch);
          }

        } else if (isChannelSwitch) {
          this.isSwitchingChannel.set(false);
          this.noResultsForChannel.set(true);
        } else {
          this.isLoadingInitialData.set(false);
          this.noContentForReporting.set(true);
        }

      });
      this.subscriptionList.add(validSubmittedPostsSub);

      const invalidSubmittedPostsSub = this.submittedPostsService.getInvalidSubmittedPostsForShoutBriefId(this.focussedBrief()!.shoutBrief!.id!).pipe(take(1)).subscribe((invalidSubmittedPosts: SubmittedPostDto[]) => {
        this.invalidSubmittedPosts.set(invalidSubmittedPosts);
      });
      this.subscriptionList.add(invalidSubmittedPostsSub);
    } else {
      this.isLoadingInitialData.set(false);
      this.isSwitchingChannel.set(false);
      this.noContentForReporting.set(true);
    }
  }

  /** Loads the brief's posts and results once; switching channel or piece filters what's loaded. */
  private getPieceResults(isChannelSwitch: boolean): void {
    const loaded = this.pieceResults();
    if (loaded) {
      this.showPieceResults(loaded, isChannelSwitch);
      return;
    }
    const briefId = this.focussedBrief()?.id;
    if (!briefId) {
      return;
    }
    this.subscriptionList.add(
      this.ultraSocialService.getBriefResults(briefId).pipe(take(1)).subscribe({
        next: (res) => {
          this.pieceResults.set(res);
          this.invalidSubmittedPosts.set(res.invalidSubmittedPosts);
          this.showPieceResults(res, isChannelSwitch);
        },
        error: () => {
          this.isLoadingInitialData.set(false);
          this.isSwitchingChannel.set(false);
          this.noContentForReporting.set(true);
        },
      }),
    );
  }

  private showPieceResults(res: BriefResultsDto, isChannelSwitch: boolean): void {
    if (!res.submittedPosts.length) {
      this.submittedPosts.set([]);
      this.isLoadingInitialData.set(false);
      this.isSwitchingChannel.set(false);
      this.noContentForReporting.set(true);
      return;
    }
    const posts = filterPiecePosts(res.submittedPosts, {
      channel: this.selectedChannelTab(),
      pieceLabel: this.selectedPiece(),
    });
    this.submittedPosts.set(posts);
    if (!posts.length) {
      this.shoutSuperSocialResultsRes.set(undefined);
      this.isLoadingInitialData.set(false);
      this.isSwitchingChannel.set(false);
      this.noResultsForChannel.set(true);
      return;
    }
    this.applyStoryMetricsThenSetResults(pieceResultsFor(res.results, posts), posts, posts, isChannelSwitch);
  }

  setSelectedPiece(pieceLabel: string | null): void {
    if (pieceLabel === this.selectedPiece()) {
      return;
    }
    this.selectedPiece.set(pieceLabel);
    this.shoutSuperSocialResultsRes.set(undefined);
    this.noResultsForChannel.set(false);
    this.getSubmittedPosts(true);
  }

  getPieceTabClass(pieceLabel: string | null) {
    if (this.selectedPiece() === pieceLabel) {
      return 'bg-fuchsia-600 text-white';
    }
    return 'bg-white dark:bg-gray-600 text-secondary hover:bg-gray-100 dark:hover:bg-gray-500';
  }

  getUltraSocialResults(isChannelSwitch = false) {
    console.log('🤩🤩🤩 getUltraSocialResults 🤩🤩🤩 : ', this.submittedPosts());
    if (this.submittedPosts() && this.submittedPosts()!.length > 0) {
      const postIds: string[] = this.submittedPosts()!.map(post => post.ss_post_id!).filter(postId => postId !== undefined);
      console.log('🤩🤩🤩 postIds 🤩🤩🤩 : ', postIds);

      const ultraSocialResultsSub = this.ultraSocialService.getCompleteLatestResultsForPosts(postIds).pipe(take(1)).subscribe((utlraSocialResults: { results: any, submittedPosts: SubmittedPostDto[] }) => {
        console.log('🤩🤩🤩 ultraSocialResults 🤩🤩🤩 ', utlraSocialResults);

        if (utlraSocialResults.results && Object.keys(utlraSocialResults.results).length > 0) {

          console.log('🤩 If statement check: ', utlraSocialResults.results && Object.keys(utlraSocialResults.results).length > 0);

          this.shoutSuperSocialResultsRes.set(utlraSocialResults);
          this.noContentForReporting.set(false);
          this.isLoadingInitialData.set(false);
          this.isSwitchingChannel.set(false);
          this.noResultsForChannel.set(false);
          this.tryOpenFeedbackForCreator(this.routeCreatorId());
        } else if (isChannelSwitch) {
          this.isSwitchingChannel.set(false);
          this.noResultsForChannel.set(true);
        } else {
          this.isLoadingInitialData.set(false);
          this.noContentForReporting.set(true);
        }
      });
      this.subscriptionList.add(ultraSocialResultsSub);
    }
  }

  getSuperSocialResults(isChannelSwitch = false) {
    const submittedPosts = this.submittedPosts() ?? [];
    if (submittedPosts.length === 0) {
      return;
    }

    const postIds = superSocialLookupIds(submittedPosts);
    const superSocial$ = postIds.length > 0
      ? this.superSocialService.getLatestResultsForPosts(postIds)
      : of({});

    const superSocialResultsSub = superSocial$.pipe(take(1)).subscribe({
      next: (superSocialResults: unknown) => {
        const ssResults = superSocialResults && typeof superSocialResults === 'object' && !Array.isArray(superSocialResults)
          ? superSocialResults as ReportingResultsMap
          : {};
        const remapped = indexSuperSocialResultsBySubmittedPost(ssResults, submittedPosts);
        const merged = dedupeReportingResultsBySubmittedPost(
          mergeSubmittedPostsIntoResults(remapped, submittedPosts),
        );
        this.applyStoryMetricsThenSetResults(merged, submittedPosts, submittedPosts, isChannelSwitch);
      },
      error: () => {
        const merged = dedupeReportingResultsBySubmittedPost(
          mergeSubmittedPostsIntoResults({}, submittedPosts),
        );
        this.applyStoryMetricsThenSetResults(merged, submittedPosts, submittedPosts, isChannelSwitch);
      },
    });
    this.subscriptionList.add(superSocialResultsSub);
  }

  private applyStoryMetricsThenSetResults(
    results: ReportingResultsMap,
    submittedPosts: SubmittedPostDto[],
    superSocialSubmittedPosts: SubmittedPostDto[],
    isChannelSwitch: boolean,
  ): void {
    const storyIds = storySubmittedPostIds(submittedPosts);
    if (storyIds.length === 0) {
      this.setReportingResults(results, superSocialSubmittedPosts, isChannelSwitch);
      return;
    }

    const phylloSub = this.phylloResultsService.getLatestSubmittedPostArray(storyIds).pipe(take(1)).subscribe({
      next: (phylloResults: ResultDto[]) => {
        this.setReportingResults(
          applyCreatorNamesToResults(
            applyPhylloMetricsToResults(results, phylloResults),
            submittedPosts,
          ),
          superSocialSubmittedPosts,
          isChannelSwitch,
        );
      },
      error: () => {
        this.setReportingResults(results, superSocialSubmittedPosts, isChannelSwitch);
      },
    });
    this.subscriptionList.add(phylloSub);
  }

  private setReportingResults(
    results: ReportingResultsMap,
    submittedPostsFromApi: SubmittedPostDto[],
    isChannelSwitch: boolean,
  ): void {
    if (Object.keys(results).length > 0 || (this.submittedPosts()?.length ?? 0) > 0) {
      this.shoutSuperSocialResultsRes.set({
        results,
        submittedPosts: submittedPostsFromApi,
      });
      this.noContentForReporting.set(false);
      this.isLoadingInitialData.set(false);
      this.isSwitchingChannel.set(false);
      this.noResultsForChannel.set(Object.keys(results).length === 0);
      this.tryOpenFeedbackForCreator(this.routeCreatorId());
      return;
    }

    if (isChannelSwitch) {
      this.isSwitchingChannel.set(false);
      this.noResultsForChannel.set(true);
      return;
    }

    this.isLoadingInitialData.set(false);
    this.noContentForReporting.set(true);
  }


  setResultsViewTab(tab: 0 | 1 | 2 | 3) {
    this.resultsViewTab.set(tab);
  }

  getResultsTabClass(tab: 0 | 1 | 2 | 3) {
    if (this.resultsViewTab() === tab) {
      return 'text-primary';
    }
    return 'text-secondary';
  }

  getTabBorderClass(tab: 0 | 1 | 2 | 3) {
    if (this.resultsViewTab() === tab) {
      return 'bg-primary';
    }
    return 'bg-transparent';
  }

  setSelectedChannel(plt: number | string) {
    const channel = plt === 'All' ? -1 : Number(plt);

    if (channel === this.selectedChannelTab()) {
      return;
    }

    this.selectedChannelTab.set(channel);
    this.shoutSuperSocialResultsRes.set(undefined);
    this.submittedPosts.set(undefined);
    this.noResultsForChannel.set(false);
    this.isSwitchingChannel.set(true);

    this.getSubmittedPosts(true);
  }

  getPlatformTabClass(plt: number | string) {
    const channel = plt === 'All' ? -1 : Number(plt);
    if (this.selectedChannelTab() === channel) {
      return 'bg-fuchsia-600 text-white';
    }
    return 'bg-white dark:bg-gray-600 text-secondary hover:bg-gray-100 dark:hover:bg-gray-500';
  }

  getChannelName(plt: number | string) {
    const numberPlt = Number(plt);
    switch (numberPlt) {
      case SocialChannelEnum['TikTok Post']:
        return 'TikTok';
      case SocialChannelEnum['Instagram Reel']:
        return 'Instagram Reel';
      case SocialChannelEnum['Instagram Story']:
        return 'Instagram Story';
      case SocialChannelEnum['YouTube Shorts']:
        return 'YouTube Shorts';
      case SocialChannelEnum['Snapchat Spotlight']:
        return 'Snapchat Spotlight';
      case SocialChannelEnum['Snapchat Story']:
        return 'Snapchat Story';
      default:
        return 'UGC Only';
    }
  }

  getNumber(value: any): number {
    return Number(value);
  }

  jumpToContent(event: LatestPostResultsDto) {
    const creator = event.submittedPost?.creator;
    this.creator.set(creator);
    this.videoId.set(event.submittedPost?.videoId);
    this.briefId.set(event.submittedPost?.briefId);
    const creatorId = creator?.blazeFirebaseUid?.trim();
    if (creatorId) {
      this.manageBriefTabsService.navigateToVideoFeedback(creatorId);
    }
  }

  closeVideoFeedback(): void {
    this.videoId.set(undefined);
    this.creator.set(undefined);
    this.briefId.set(undefined);
    this.manageBriefTabsService.clearVideoFeedbackRoute();
  }

  private tryOpenFeedbackForCreator(creatorId: string | undefined): void {
    if (!creatorId) {
      return;
    }
    if (this.creator()?.blazeFirebaseUid === creatorId && this.videoId()) {
      return;
    }

    const results = this.shoutSuperSocialResultsRes()?.results;
    if (!results) {
      return;
    }

    const match = (Object.values(results) as LatestPostResultsDto[]).find(
      (post) => post.submittedPost?.creator?.blazeFirebaseUid === creatorId,
    );
    if (!match?.submittedPost) {
      return;
    }

    this.creator.set(match.submittedPost.creator);
    this.videoId.set(match.submittedPost.videoId);
    this.briefId.set(match.submittedPost.briefId);
  }

  refreshBriefResults(): void {
    const brief = this.focussedBrief();
    if (!brief?.id) {
      return;
    }
    this.refreshResultsToasts.start(brief.id, brief.title ?? 'Brief');
  }

  downloadResultsCsv() {
    console.log('download csv', this.shoutSuperSocialResultsRes);
    const donloadPosts = this.submittedPosts()!.map((post: SubmittedPostDto) => {
      const result = resultForSubmittedPost(this.shoutSuperSocialResultsRes()!.results, post);
      if (!result) {
        return {
          postId: reportingResultKey(post),
          link: post.fullUrl || post.videoLink,
          adcode: post.adCode,
          status: post.status,
          views: '',
          likes: '',
          shares: '',
          comments: '',
          saves: '',
          updatedDate: ''
        }
      }
      else {
        return {
          postId: reportingResultKey(post),
          link: post.fullUrl || post.videoLink,
          adcode: post.adCode,
          status: post.status,
          views: result.views,
          likes: result.likes,
          shares: result.shares,
          comments: result.comments,
          saves: result.save_count,
          updatedDate: result.updatedDate
        }
      }

    });
    console.log('DOWNLOAD POSTS', donloadPosts);
    this.downloadCsv(donloadPosts, `${this.focussedBrief()?.title}.csv`)
  }

  downloadCsv(data: any[], filename: string = 'data.csv'): void {
    if (!data || data.length === 0) {
      console.error('No data provided for CSV download.');
      return;
    }

    // 1. Convert JSON array to CSV string
    const csvContent = this.convertToCsv(data);

    // 2. Trigger the download
    this.initiateDownload(csvContent, filename);
  }

  /**
   * Internal method to convert JSON array to a CSV string.
   * Assumes all objects in the array have the same keys/structure.
   * @param data The JSON array.
   * @returns The generated CSV content string.
   */
  private convertToCsv(data: any[]): string {
    const replacer = (_key: any, value: any) => (value === null || value === undefined) ? '' : value; // Handle null/undefined values

    // Get headers (keys of the first object)
    const header = Object.keys(data[0]);

    // Create the header row
    let csv = header.join(',') + '\n';

    // Create the data rows
    const rows = data.map(row =>
      header.map(fieldName => JSON.stringify(row[fieldName], replacer)).join(',')
    );

    csv += rows.join('\n');
    return csv;
  }

  /**
   * Internal method to create a Blob and trigger the file download in the browser.
   * @param content The CSV string content.
   * @param filename The name of the file.
   */
  private initiateDownload(content: string, filename: string): void {
    // Create a Blob with the CSV content and specify the MIME type
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });

    // Create a temporary URL for the blob
    const url = URL.createObjectURL(blob);

    // Create a temporary <a> element to trigger the download
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename); // Set the filename

    // Append to the document, click, and remove it
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Revoke the object URL to free up memory
    URL.revokeObjectURL(url);
  }

  canManageReportSchedules(): boolean {
    return this.currentUser.hasAnyRole(REPORT_SCHEDULE_MANAGER_ROLES);
  }

  loadReportSchedule(): void {
    const briefId = this.focussedBriefId();
    if (!this.reportsEnabled || !briefId || !this.canManageReportSchedules()) {
      this.reportSchedule.set(null);
      return;
    }
    this.subscriptionList.add(
      this.reportSchedules.get(briefId).subscribe({
        next: (schedule) => {
          this.reportSchedule.set(schedule);
          this.posthog.posthog.capture('report_schedule_cta_viewed', { hasSchedule: schedule !== null });
          this.cdr.detectChanges();
          this.openScheduleFromQuery();
        },
        error: () => {
          this.reportSchedule.set(null);
          this.posthog.posthog.capture('report_schedule_cta_viewed', { hasSchedule: false });
          this.cdr.detectChanges();
          this.openScheduleFromQuery();
        },
      }),
    );
  }

  openScheduleDialog(source: 'banner' | 'pill'): void {
    const briefId = this.focussedBriefId();
    if (!briefId || !this.canManageReportSchedules()) {
      return;
    }
    this.posthog.posthog.capture('report_schedule_cta_clicked', { source });
    void firstValueFrom(this.firebase.user$).then((user) => {
      this.scheduleDialog.set({
        briefId,
        briefName: this.focussedBrief()?.title ?? 'Brief',
        schedule: this.reportSchedule(),
        currentUserEmail: user?.email ?? '',
      });
      this.cdr.detectChanges();
    });
  }

  onScheduleDialogClosed(result: ReportScheduleDialogResult | undefined): void {
    this.scheduleDialog.set(null);
    if (!result) {
      this.cdr.detectChanges();
      return;
    }
    if (result.action === 'saved') {
      this.reportSchedule.set(result.schedule);
      this.posthog.posthog.capture('report_schedule_saved', {
        day: result.schedule.dayOfWeek,
        time: result.schedule.timeOfDay,
        tz: result.schedule.timezone,
        recipientCount: result.schedule.recipientCount,
        sections: result.schedule.sections,
      });
      this.toast.alert({ title: 'Weekly report scheduled', message: result.schedule.nextRunLocal, type: 'success', icon: 'success' });
    } else {
      this.reportSchedule.set(null);
      this.posthog.posthog.capture('report_schedule_stopped');
      this.toast.alert({ title: 'Weekly report stopped', message: 'No further reports will be sent.', type: 'info', icon: 'info' });
    }
    this.cdr.detectChanges();
  }

  previewReport(): void {
    const briefId = this.focussedBriefId();
    if (!briefId || !this.canManageReportSchedules()) {
      return;
    }
    this.posthog.posthog.capture('report_schedule_cta_clicked', { source: 'banner' });
    this.subscriptionList.add(
      this.reportSchedules.preview(briefId, browserTimeZone()).subscribe({
        next: (result) => {
          const url = result.url.startsWith('http') ? result.url : `${window.location.origin}${result.url}`;
          window.open(url, '_blank', 'noopener');
        },
        error: () => {
          this.toast.alert({ title: 'Preview unavailable', message: 'Could not open a preview of this report.', type: 'error', icon: 'error' });
          this.cdr.detectChanges();
        },
      }),
    );
  }

  viewLatestReport(): void {
    if (!this.canManageReportSchedules()) {
      return;
    }
    const url = this.reportSchedule()?.latestReportUrl;
    if (!url) {
      this.toast.alert({ title: 'No report yet', message: 'The first report is sent at the scheduled time. You can preview it now.', type: 'info', icon: 'info' });
      return;
    }
    const absolute = url.startsWith('http') ? url : `${window.location.origin}${url}`;
    window.open(absolute, '_blank', 'noopener');
  }

  private openScheduleFromQuery(): void {
    if (this.openedScheduleFromQuery || this.route.snapshot.queryParamMap.get('schedule') !== 'edit') {
      return;
    }
    this.openedScheduleFromQuery = true;
    this.openScheduleDialog('banner');
  }
}
